import { format, type SqlLanguage } from 'sql-formatter';

/**
 * Pure helpers for the SQL formatter tool.
 *
 * Wraps `sql-formatter` for the format path and hand-rolls a small
 * minifier for the compress path (sql-formatter doesn't ship one).
 *
 * We don't validate SQL syntax — sql-formatter itself is forgiving and
 * will pretty-print whatever it can lex; the design doc §10 explicitly
 * scopes validation out (would need a per-dialect parser, complexity L).
 */

/**
 * Dialect ids accepted by `sql-formatter` v15. We surface the most
 * common ones with friendly labels (see `SQL_DIALECT_LABEL`); niche
 * dialects (n1ql / hive / db2i / etc.) stay reachable but unlabelled.
 *
 * Renames vs the design doc (which targeted v12 names):
 *   `mssql`  → `tsql`       (Transact-SQL)
 *   `oracle` → `plsql`      (PL/SQL)
 */
export const SQL_DIALECTS = [
  'postgresql',
  'mysql',
  'mariadb',
  'sqlite',
  'tsql',
  'plsql',
  'bigquery',
  'snowflake',
  'redshift',
  'db2',
  'clickhouse',
  'duckdb',
  'spark',
  'trino',
] as const;

export type SqlDialect = (typeof SQL_DIALECTS)[number];

/** Friendly human-readable label for a dialect (UI dropdowns). */
export const SQL_DIALECT_LABEL: Record<SqlDialect, string> = {
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  sqlite: 'SQLite',
  tsql: 'SQL Server (T-SQL)',
  plsql: 'Oracle (PL/SQL)',
  bigquery: 'BigQuery',
  snowflake: 'Snowflake',
  redshift: 'Redshift',
  db2: 'DB2',
  clickhouse: 'ClickHouse',
  duckdb: 'DuckDB',
  spark: 'Spark SQL',
  trino: 'Trino / Presto',
};

export type KeywordCase = 'upper' | 'lower' | 'preserve';

export interface SqlFormatOptions {
  dialect: SqlDialect;
  /** Keyword case. Default 'upper'. */
  keywordCase?: KeywordCase;
  /** Tab width (1..8). Default 2. */
  tabWidth?: number;
}

export interface FormatOk {
  ok: true;
  output: string;
}

export interface FormatErr {
  ok: false;
  error: string;
}

export type SqlResult = FormatOk | FormatErr;

const DEFAULT_OPTS: Required<Pick<SqlFormatOptions, 'keywordCase' | 'tabWidth'>> = {
  keywordCase: 'upper',
  tabWidth: 2,
};

/** Format SQL using `sql-formatter` with the given dialect / case / indent. */
export function formatSql(input: string, opts: SqlFormatOptions): SqlResult {
  if (input.trim().length === 0) return { ok: true, output: '' };
  try {
    const out = format(input, {
      language: opts.dialect as SqlLanguage,
      keywordCase: opts.keywordCase ?? DEFAULT_OPTS.keywordCase,
      tabWidth: clampTabWidth(opts.tabWidth),
    });
    return { ok: true, output: out };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/**
 * Compress SQL to a single-line form. Whitespace inside string
 * literals (single / double quote, backtick) and comments (`--` line,
 * `/* ... *​/` block) is preserved exactly; whitespace outside is
 * collapsed to a single space.
 *
 * Returns `{ ok: true }` even on weird inputs — same forgiving stance
 * as `formatSql`. Trailing semicolon-only inputs collapse to ''.
 */
export function minifySql(input: string): SqlResult {
  if (input.trim().length === 0) return { ok: true, output: '' };

  let out = '';
  let i = 0;
  const len = input.length;

  while (i < len) {
    const ch = input[i];

    // -- single-line comment: keep it AS IS up to the end of line, but
    // remember we now need a separator so the next bit doesn't merge
    // into the comment text.
    if (ch === '-' && input[i + 1] === '-') {
      const eol = input.indexOf('\n', i);
      const end = eol === -1 ? len : eol;
      out += input.slice(i, end);
      // Insert a newline so subsequent SQL doesn't fall victim to the
      // comment swallowing it. The newline is the only multiline thing
      // we emit; downstream collapse leaves it alone.
      if (eol !== -1) out += '\n';
      i = end + (eol === -1 ? 0 : 1);
      continue;
    }

    // /* ... */ block comment: keep verbatim.
    if (ch === '/' && input[i + 1] === '*') {
      const end = input.indexOf('*/', i + 2);
      if (end === -1) {
        // Unterminated comment — copy the rest and stop.
        out += input.slice(i);
        break;
      }
      out += input.slice(i, end + 2);
      i = end + 2;
      continue;
    }

    // String literal: keep contents verbatim; handle doubled-quote
    // escape (`'O''Brien'`).
    if (ch === "'" || ch === '"' || ch === '`') {
      const quote = ch;
      out += quote;
      i++;
      while (i < len) {
        const c = input[i];
        if (c === quote) {
          // Doubled-quote escape: 'O''Brien'.
          if (input[i + 1] === quote) {
            out += quote + quote;
            i += 2;
            continue;
          }
          out += quote;
          i++;
          break;
        }
        if (c === '\\') {
          // Backslash escape (MySQL etc.) — copy the next char too.
          out += c;
          if (i + 1 < len) {
            out += input[i + 1];
            i += 2;
          } else {
            i++;
          }
          continue;
        }
        out += c;
        i++;
      }
      continue;
    }

    // Whitespace run outside literal/comment: compress to single space.
    if (/\s/.test(ch)) {
      while (i < len && /\s/.test(input[i])) i++;
      // Trim the leading/trailing space sometime later — for now emit
      // a single space.
      if (out.length > 0 && out[out.length - 1] !== ' ' && out[out.length - 1] !== '\n') {
        out += ' ';
      }
      continue;
    }

    out += ch;
    i++;
  }

  return { ok: true, output: out.trim() };
}

function clampTabWidth(n: number | undefined): number {
  if (n === undefined) return DEFAULT_OPTS.tabWidth;
  if (n < 1) return 1;
  if (n > 8) return 8;
  return Math.floor(n);
}
